import { Certificate, CertificateStatus } from '@/types/certificate';
import { createClient } from '@/lib/supabase/client';

// ─── DB row shape (snake_case) ────────────────────────────────────────────────
interface CertificateRow {
  id: string;
  certificate_id: string;
  holder_name: string;
  company_name: string;
  standard: string;
  issue_date: string;
  expiry_date: string;
  cert_status: string;
  notes: string;
  file_name: string;
  file_url: string;
  file_type: string;
  uploaded_at: string;
}

// ─── Activity log helper ──────────────────────────────────────────────────────
async function logActivity(
  action: 'upload' | 'edit' | 'delete',
  cert: { certificateId: string; holderName: string; companyName: string; standard: string },
  details?: string
): Promise<void> {
  try {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    await supabase.from('activity_logs').insert({
      action,
      certificate_id: cert.certificateId,
      holder_name: cert.holderName,
      company_name: cert.companyName,
      standard: cert.standard,
      performed_by_email: user?.email ?? 'unknown',
      performed_by_id: user?.id ?? null,
      details: details ?? '',
    });
  } catch (err) {
    // Non-blocking: log failure should not break the main operation
    console.warn('Activity log failed:', err);
  }
}

// ─── Mappers ──────────────────────────────────────────────────────────────────
function rowToCertificate(row: CertificateRow): Certificate {
  return {
    id: row.id,
    certificateId: row.certificate_id,
    holderName: row.holder_name,
    companyName: row.company_name,
    standard: row.standard,
    issueDate: row.issue_date,
    expiryDate: row.expiry_date,
    status: computeStatus(row.expiry_date),
    notes: row.notes ?? '',
    fileName: row.file_name ?? '',
    fileData: row.file_url ?? '',   // fileData now holds the public URL
    fileType: row.file_type ?? '',
    uploadedAt: row.uploaded_at,
  };
}

// ─── Status computation ───────────────────────────────────────────────────────
export function computeStatus(expiryDate: string): CertificateStatus {
  const now = new Date();
  const expiry = new Date(expiryDate);
  const diffDays = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return 'Expired';
  if (diffDays <= 90) return 'Expiring Soon';
  return 'Active';
}

// ─── Date formatter ───────────────────────────────────────────────────────────
export function formatDate(dateStr: string): string {
  if (!dateStr) return '—';
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
}

// ─── ID generator (kept for compatibility) ────────────────────────────────────
export function generateId(): string {
  return `cert-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
}

// ─── No-op seed (data lives in Supabase now) ──────────────────────────────────
export function seedCertificates(): void {
  // No-op: data is seeded via Supabase migration
}

// ─── Fetch all certificates ───────────────────────────────────────────────────
export async function getAllCertificates(): Promise<Certificate[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('certificates')
    .select('*')
    .order('uploaded_at', { ascending: false });

  if (error) {
    console.error('getAllCertificates error:', error.message);
    return [];
  }
  return (data as CertificateRow[]).map(rowToCertificate);
}

// ─── Search by certificate ID ─────────────────────────────────────────────────
export async function getCertificateById(certId: string): Promise<Certificate | null> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('certificates')
    .select('*')
    .ilike('certificate_id', certId.trim())
    .maybeSingle();

  if (error) {
    console.error('getCertificateById error:', error.message);
    return null;
  }
  if (!data) return null;
  return rowToCertificate(data as CertificateRow);
}

// ─── Upload file to Supabase Storage ─────────────────────────────────────────
export async function uploadCertificateFile(
  file: File,
  certificateId: string
): Promise<{ fileUrl: string; fileName: string; fileType: string } | null> {
  const supabase = createClient();
  const ext = file.name.split('.').pop() ?? 'pdf';
  const path = `certificates/${certificateId}-${Date.now()}.${ext}`;

  const { error: uploadError } = await supabase.storage
    .from('certificate-files')
    .upload(path, file, { cacheControl: '3600', upsert: true });

  if (uploadError) {
    console.error('uploadCertificateFile error:', uploadError.message);
    throw new Error(`File upload failed: ${uploadError.message}`);
  }

  const { data: { publicUrl } } = supabase.storage
    .from('certificate-files')
    .getPublicUrl(path);

  return { fileUrl: publicUrl, fileName: file.name, fileType: file.type };
}

// ─── Save (upsert) certificate ────────────────────────────────────────────────
export async function saveCertificate(cert: Certificate): Promise<void> {
  const supabase = createClient();

  const isNew = !cert.id || cert.id.startsWith('cert-');

  const row = {
    id: cert.id && !cert.id.startsWith('cert-') ? cert.id : undefined,
    certificate_id: cert.certificateId.trim().toUpperCase(),
    holder_name: cert.holderName,
    company_name: cert.companyName,
    standard: cert.standard,
    issue_date: cert.issueDate,
    expiry_date: cert.expiryDate,
    cert_status: computeStatus(cert.expiryDate) as string,
    notes: cert.notes ?? '',
    file_name: cert.fileName ?? '',
    file_url: cert.fileData ?? '',
    file_type: cert.fileType ?? '',
    uploaded_at: cert.uploadedAt ?? new Date().toISOString(),
  };

  // Remove undefined id so Supabase generates one on insert
  if (!row.id) delete (row as any).id;

  const { error } = await supabase
    .from('certificates')
    .upsert(row, { onConflict: 'certificate_id' });

  if (error) {
    console.error('saveCertificate error:', error.message);
    throw new Error(error.message);
  }

  // Log the activity after successful save
  await logActivity(
    isNew ? 'upload' : 'edit',
    {
      certificateId: cert.certificateId.trim().toUpperCase(),
      holderName: cert.holderName,
      companyName: cert.companyName,
      standard: cert.standard,
    },
    isNew ? 'Certificate uploaded' : 'Certificate details updated'
  );
}

// ─── Delete certificate ───────────────────────────────────────────────────────
export async function deleteCertificate(id: string, cert?: { certificateId: string; holderName: string; companyName: string; standard: string }): Promise<void> {
  const supabase = createClient();

  // Fetch cert details for logging if not provided
  let logData = cert;
  if (!logData) {
    const { data } = await supabase
      .from('certificates')
      .select('certificate_id, holder_name, company_name, standard')
      .eq('id', id)
      .maybeSingle();
    if (data) {
      logData = {
        certificateId: data.certificate_id,
        holderName: data.holder_name,
        companyName: data.company_name,
        standard: data.standard,
      };
    }
  }

  const { error } = await supabase
    .from('certificates')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('deleteCertificate error:', error.message);
    throw new Error(error.message);
  }

  // Log the deletion
  if (logData) {
    await logActivity('delete', logData, 'Certificate deleted');
  }
}